// ViewBills Component - Tabs view updated
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './ViewBills.css';
import { billsApi, customerApi } from '../../services/smartBillApi';
import { paymentsApi, openRazorpayCheckout } from '../../services/paymentsApi';
import { parseAppTimestamp, formatInIst } from '../../utils/istDateTime';
import { buildCustomerLoginUrl } from '../../services/whatsappTemplateService';
import { openCashCreditBillPrint, openCashCreditBillPrintAndSend } from '../../utils/printCashCreditBill';
import ProfessionSelectField from '../../components/ProfessionSelectField'

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Date • Newest' },
  { value: 'date_asc', label: 'Date • Oldest' },
  { value: 'service_charge_desc', label: 'Service Charge • High to Low' },
  { value: 'service_charge_asc', label: 'Service Charge • Low to High' },
  { value: 'challana_desc', label: 'Challana • High to Low' },
  { value: 'challana_asc', label: 'Challana • Low to High' },
  { value: 'paid_desc', label: 'Paid • High to Low' },
  { value: 'paid_asc', label: 'Paid • Low to High' },
  { value: 'due_desc', label: 'Due • High to Low' },
  { value: 'due_asc', label: 'Due • Low to High' },
  { value: 'amount_desc', label: 'Bill Total • High to Low' },
  { value: 'amount_asc', label: 'Bill Total • Low to High' },
  { value: 'name_asc', label: 'Customer • A → Z' },
  { value: 'name_desc', label: 'Customer • Z → A' },
  { value: 'status', label: 'Status' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'partial', label: 'Partial' },
  { value: 'due', label: 'Due' },
];

const QUICK_RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'last7', label: 'Last 7 Days' },
  { id: 'last30', label: 'Last 30 Days' },
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'thisYear', label: 'This Year' },
  { id: 'custom', label: 'Custom' },
];

const TABLE_COLUMN_COUNT = 15;

/** Ref callback that keeps a textarea tall enough to show all wrapped lines. */
const autoGrowTextarea = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

/** Labels for list filters / badges. Keep all historical DB values for live data. */
const WORK_STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'under_processing', label: 'In Progress' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'advance', label: 'Advance' },
];

/** Display label for Work Status column — advance bills always show ADVANCE. */
const getWorkStatusDisplay = (bill) => {
  if (bill?.isAdvance) {
    return { key: 'advance', label: 'ADVANCE' };
  }
  const status = bill?.workStatus || 'completed';
  return {
    key: status,
    label: WORK_STATUS_OPTIONS.find((opt) => opt.value === status)?.label || status,
  };
};

/** Edit modal: industry-simple Pending / Completed only (DB still accepts legacy values). */
const EDIT_WORK_STATUS_OPTIONS = [
  {
    value: 'pending',
    label: 'Pending',
    description: 'Follow-up needed before delivery',
  },
  {
    value: 'completed',
    label: 'Completed',
    description: 'Work finished & delivered',
  },
];

const PENDING_REASON_CHIPS = [
  'Customer documents pending',
  'Payment pending',
  'Awaiting customer callback',
  'Portal / department follow-up',
  'Signature / verification pending',
];

/** Prefilled when Pending is selected so staff can type the reason immediately. */
const DEFAULT_PENDING_REMARK = 'default pending reason : ';

const applyPendingDefaultRemark = (remark = '') => {
  const trimmed = String(remark || '').trim();
  return trimmed ? trimmed : DEFAULT_PENDING_REMARK;
};

const isPendingRemarkIncomplete = (remark = '') => {
  const trimmed = String(remark || '').trim();
  return !trimmed || trimmed === DEFAULT_PENDING_REMARK.trim();
};

const CANCEL_REASON_CHIPS = [
  'Customer documents not available',
  'Customer requested refund',
  'Application rejected / not possible',
  'Wrong service selected',
  'Duplicate bill',
];

const DEFAULT_CANCEL_PROCESSING_FEE = 100;

/** Strip old auto-filled boilerplate so counters write real notes. */
const LEGACY_AUTO_REMARKS = new Set([
  'work delivered successfully.',
  'waiting for customer response/documents.',
  'processing with department/portal.',
  'cancelled based on customer instruction.',
  'pf claim submitted - awaiting portal approval.',
  'online application processing - will update once submitted.',
]);

const sanitizeWorkRemark = (remark = '') => {
  const trimmed = String(remark || '').trim();
  if (!trimmed) return '';
  return LEGACY_AUTO_REMARKS.has(trimmed.toLowerCase()) ? '' : trimmed;
};

/** Map legacy statuses into the edit radios without rewriting DB until Save. */
const normalizeEditWorkStatus = (status) => {
  if (status === 'pending' || status === 'under_processing') return 'pending';
  if (status === 'cancelled') return 'cancelled';
  return 'completed';
};

/** Service names for View Bills → Enquiry Form prefill. */
const buildEnquiryWorkPurpose = (bill) => {
  const services = Array.isArray(bill?.services) ? bill.services : [];
  const names = services
    .map((service) =>
      String(
        service?.display_name || service?.service_name || service?.name || '',
      ).trim(),
    )
    .filter(Boolean);
  return names.length ? names.join(', ') : '';
};

const formatServiceNames = (services = []) => {
  const names = (services || [])
    .map((s) => (s.name || s.service_name || s.display_name || '').trim())
    .filter(Boolean);
  if (names.length === 0) return 'No service listed';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} · ${names[1]}`;
  return `${names[0]} · ${names[1]} +${names.length - 2} more`;
};

const rangeLabelMap = QUICK_RANGES.reduce((acc, item) => {
  acc[item.id] = item.label;
  return acc;
}, {});

const parsePaymentMethods = (methods) => {
  if (!methods) return {};
  if (typeof methods === 'string') {
    try {
      return JSON.parse(methods);
    } catch (err) {
      console.error('Failed to parse payment methods', err);
      return {};
    }
  }
  return methods;
};

const mapBill = (bill) => {
  const paymentMethods = parsePaymentMethods(bill.payment_methods || bill.paymentMethods);
  const services = (bill.services || []).map((service) => ({
    id: service.id,
    name: service.display_name || service.service_name,
    quantity: service.quantity || 1,
    total: Number(service.total || 0),
    serviceCharge: Number(service.service_charge || 0),
    challanaAmount: Number(service.challana_amount || 0),
    gstAmount: Number(service.gst_amount ?? service.gstAmount ?? 0),
    otherTaxAmount: Number(service.other_tax_amount ?? service.otherTaxAmount ?? 0),
    hsnCode: service.hsn_code || service.hsnCode || '',
  }));
  const offers = (bill.offers || []).map((offer) => ({
    id: offer.id,
    name: offer.name,
    savings: Number(offer.total_savings || 0),
  }));

  const rawBillType = (bill.bill_type || bill.billType || '').toString().toLowerCase();
  const inferredBillType =
    rawBillType === 'coupon' || rawBillType === 'regular'
      ? rawBillType
      : offers.length > 0
      ? 'coupon'
      : 'regular';

  return {
    id: bill.id,
    billNo: bill.bill_number || bill.billNo,
    customerName: bill.customer_name || bill.customerName,
    customerPhone: bill.customer_phone || bill.customerPhone,
    customerAddress: bill.customer_address || bill.customerAddress || '',
    customerCity: bill.customer_city || bill.customerCity || '',
    customerPincode: bill.customer_pincode || bill.customerPincode || '',
    billDate: bill.bill_date || bill.billDate,
    totalAmount: Number(bill.total_amount ?? bill.totalAmount ?? bill.amount ?? 0),
    paidAmount: Number(bill.paid_amount ?? bill.paidAmount ?? 0),
    dueAmount: Number(bill.due_amount ?? bill.dueAmount ?? 0),
    paymentStatus: bill.payment_status || bill.status || (Number(bill.due_amount ?? bill.dueAmount ?? 0) > 0 ? 'due' : 'paid'),
    paymentMethods,
    services,
    offers,
    billType: inferredBillType,
    remarks: bill.remarks || '',
    discountAmount: Number(bill.discount_amount ?? 0),
    couponDiscount: Number(bill.coupon_discount ?? 0),
    workStatus: bill.work_status || bill.workStatus || 'completed',
    workRemark: bill.work_remark || bill.workRemark || '',
    isAdvance: !!(bill.is_advance ?? bill.isAdvance),
    salesCountedAt: bill.sales_counted_at || bill.salesCountedAt || null,
    processingFeeRetained: Number(bill.processing_fee_retained ?? bill.processingFeeRetained ?? 0),
    refundAmount: Number(bill.refund_amount ?? bill.refundAmount ?? 0),
    refundMethod: bill.refund_method || bill.refundMethod || '',
    refundReason: bill.refund_reason || bill.refundReason || '',
    refundBucket: bill.refund_bucket || bill.refundBucket || '',
    refundedAt: bill.refunded_at || bill.refundedAt || null,
    createdAt:
      bill.bill_date ||
      bill.billDate ||
      bill.created_at ||
      bill.createdAt,
    updatedAt: bill.updated_at || bill.updatedAt,
    createdByStamp: bill.created_by_stamp || bill.createdByStamp || '—',
    billOrigin: String(bill.billOrigin || bill.bill_origin || '').toLowerCase() === 'order' ? 'order' : 'counter',
    billOriginLabel:
      bill.billOriginLabel ||
      bill.bill_origin_label ||
      (String(bill.billOrigin || bill.bill_origin || '').toLowerCase() === 'order'
        ? 'Order bill'
        : 'Counter bill'),
  };
};

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value) || 0);

const parseBillTimestamp = parseAppTimestamp;

const getTenantScopeFromSession = () => {
  // Tenant scoping is enforced by backend from the decoded auth token.
  // Keep request params free of client-controlled tenant filters.
  return {};
};

const formatBillDate = (dateString) => formatInIst(dateString, { withSeconds: false });

const formatDateTimeWithSeconds = (dateString) =>
  formatInIst(dateString, { withSeconds: true });

const getPrimaryServiceInfo = (bill) => {
  if (bill?.services?.length) {
    const [firstService, ...rest] = bill.services;
    return {
      label: firstService.name || '—',
      extraCount: rest.length,
      isCoupon: false,
    };
  }

  if (bill?.offers?.length) {
    const [firstOffer, ...restOffers] = bill.offers;
    return {
      label: firstOffer?.name ? `${firstOffer.name} (Coupon)` : 'Coupon Bill',
      extraCount: restOffers.length,
      isCoupon: true,
    };
  }

  return { label: '—', extraCount: 0, isCoupon: false };
};

const getTotalServiceCharge = (bill) => {
  if (!bill?.services?.length) return 0;
  return bill.services.reduce((sum, service) => {
    const quantity = Number(service?.quantity) || 1;
    const explicitServiceCharge = Number(service?.serviceCharge || 0);
    if (explicitServiceCharge > 0) {
      // serviceCharge is stored per-unit; multiply by qty for bill total
      return sum + explicitServiceCharge * quantity;
    }

    // Fallback for older data: total - (challana × qty), ignore negative results.
    const total = Number(service?.total || 0);
    const challana = Number(service?.challanaAmount || 0);
    const inferredServiceCharge = Math.max(0, total - challana * quantity);
    return sum + inferredServiceCharge;
  }, 0);
};

const getTotalChallanaAmount = (bill) => {
  if (!bill?.services?.length) return 0;
  return bill.services.reduce((sum, service) => {
    const quantity = Number(service?.quantity) || 1;
    const challana = Number(service?.challanaAmount || 0);
    return sum + challana * quantity;
  }, 0);
};

const ViewBills = ({ advanceOnly = false } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const advanceMode =
    advanceOnly ||
    location.pathname.includes('/bills/advance') ||
    new URLSearchParams(location.search || '').get('advance') === '1';

  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({
    totalRecords: 0,
    totalBills: 0,
    totalCancelled: 0,
    totalRevenue: 0,
    totalServiceCharge: 0,
    totalChallanaAmount: 0,
    pendingAmount: 0,
    discountAmount: 0,
    couponDiscount: 0,
    statusCounts: {
      under_processing: 0,
      pending: 0,
      completed: 0,
      cancelled: 0,
    },
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workStatusFilter, setWorkStatusFilter] = useState(advanceMode ? 'advance' : 'all');
  const [sortOption, setSortOption] = useState('date_desc');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editBill, setEditBill] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [detailBillId, setDetailBillId] = useState(null);
  const [billTypeUpdatingId, setBillTypeUpdatingId] = useState(null);
  const [razorpayBusy, setRazorpayBusy] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [cancelPanelOpen, setCancelPanelOpen] = useState(false);
  const [cancelFee, setCancelFee] = useState(DEFAULT_CANCEL_PROCESSING_FEE);
  const pendingRemarkRef = useRef(null);
  const prevWorkStatusRef = useRef(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMethod, setCancelMethod] = useState('cash');
  const [cancelBucket, setCancelBucket] = useState('service_charges');
  const [cancelBusy, setCancelBusy] = useState(false);

  const [quickRange, setQuickRange] = useState('thisYear');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showServiceAmount, setShowServiceAmount] = useState(false);
  const [showBillAmount, setShowBillAmount] = useState(false);
  const [showChallanaAmount, setShowChallanaAmount] = useState(false);

  const selectedRangeLabel = quickRange === 'custom' ? 'Custom' : rangeLabelMap[quickRange] || 'All';
  const editServiceSummary = useMemo(
    () => formatServiceNames(editForm?.services || []),
    [editForm?.services]
  );

  useEffect(() => {
    if (!cancelPanelOpen || !editBill?.id || editBill.refundedAt) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await billsApi.previewCancelRefund(editBill.id, {
          processing_fee: Number(cancelFee) || DEFAULT_CANCEL_PROCESSING_FEE,
        });
        if (cancelled || !res?.success || !res.data) return;
        if (res.data.suggestedBucket === 'sales' || res.data.suggestedBucket === 'service_charges') {
          setCancelBucket(res.data.suggestedBucket);
        }
      } catch (err) {
        console.warn('cancel-refund preview:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only when panel opens / bill changes — fee edits use local calculator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelPanelOpen, editBill?.id, editBill?.refundedAt]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam && String(searchParam).trim()) {
      setSearchTerm(String(searchParam).trim());
    }
    const rangeParam = params.get('range');
    const fromParam = params.get('from');
    const toParam = params.get('to');

    if (!rangeParam && !fromParam && !toParam) return;

    const isValidRange = QUICK_RANGES.some((range) => range.id === rangeParam);

    if (rangeParam === 'custom' || (!isValidRange && (fromParam || toParam))) {
      setQuickRange('custom');
      if (fromParam) setFromDate(fromParam);
      if (toParam) setToDate(toParam);
    } else if (isValidRange && quickRange !== rangeParam) {
      setQuickRange(rangeParam);
    }

    if (fromParam && !toParam) {
      setToDate('');
    }
    if (toParam && !fromParam) {
      setFromDate('');
    }
  }, [location.search, quickRange]); 

  const fetchBills = useCallback(async (pageToLoad) => {
    const pageNum = pageToLoad !== undefined ? pageToLoad : currentPage;
    try {
      setLoading(true);
      setError(null);
      const statusParam = statusFilter === 'all' ? '' : statusFilter;
      const isAdvanceFilter = advanceMode || workStatusFilter === 'advance';
      const workStatusParam =
        isAdvanceFilter || workStatusFilter === 'all' ? '' : workStatusFilter;
      const query = {
        ...getTenantScopeFromSession(),
        page: pageNum,
        limit: pageSize,
        search: searchTerm,
        status: statusParam,
        work_status: workStatusParam,
      };
      if (isAdvanceFilter) {
        query.is_advance = true;
      }

      if (quickRange !== 'custom') {
        query.range = quickRange;
      } else {
        if (fromDate) query.from = fromDate;
        if (toDate) query.to = toDate;
      }

      console.log('📡 Fetching bills with query:', query);
      const response = await billsApi.getAll(query);
      console.log('📥 Bills API response:', { 
        success: response.success, 
        dataLength: response.data?.length, 
        pagination: response.pagination,
        summary: response.summary,
        responseKeys: Object.keys(response || {})
      });
      
      // Log API endpoint being used
      console.log('🔗 API Endpoint: /smartbills/bills with params:', JSON.stringify(query));
      
      // Debug: Check if response has data
      if (!response.data || response.data.length === 0) {
        console.warn('⚠️ No bills returned from API. Response:', {
          success: response.success,
          dataLength: response.data?.length,
          total: response.pagination?.total,
          message: response.message,
          apiUrl: 'Check console logs above for API_BASE_URL and hostname info'
        });
        
        // On Digital Ocean, if no bills are found, it might be a database sync issue
        if (typeof window !== 'undefined' && window.location.hostname.includes('.ondigitalocean')) {
          console.warn('🔗 DIGITAL OCEAN DETECTED: If bills were just saved but not showing, the API endpoint might be incorrect. Check the API_BASE_URL in the logs above.');
        }
      }
      
      // Debug: Check if first bill has services
      if (response.data && response.data.length > 0) {
        const firstBill = response.data[0];
        console.log('🔍 First bill services check:', {
          billId: firstBill.id,
          billNo: firstBill.bill_number || firstBill.billNo,
          hasServices: !!firstBill.services,
          servicesType: typeof firstBill.services,
          servicesIsArray: Array.isArray(firstBill.services),
          servicesLength: firstBill.services?.length,
          servicesData: firstBill.services,
        });
      }

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch bills');
      }

      const normalizedBills = (response.data || []).map(mapBill);
      setBills(normalizedBills);

      const apiPagination = response.pagination || {};
      setPagination({
        page: apiPagination.page || pageNum,
        pages: apiPagination.pages || 1,
        total: apiPagination.total || normalizedBills.length,
        limit: apiPagination.limit || pageSize,
      });

      const serverSummary = response.summary || {};
      let statusCounts = serverSummary.statusCounts || serverSummary.status_counts || {
        under_processing: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
      };
      if (typeof statusCounts === 'string') {
        try {
          statusCounts = JSON.parse(statusCounts);
        } catch (parseErr) {
          console.warn('Unable to parse statusCounts from API', parseErr);
          statusCounts = {
            under_processing: 0,
            pending: 0,
            completed: 0,
            cancelled: 0,
          };
        }
      }
      const totalRecordsFromApi = Number(
        serverSummary.totalRecords ??
          serverSummary.total_records ??
          apiPagination.total ??
          normalizedBills.length ??
          0,
      );
      const totalServiceCharge = Number(
        serverSummary.totalServiceCharge ??
          serverSummary.total_service_charge ??
          serverSummary.totalServiceChargeExChallana ??
          serverSummary.total_service_charge_ex_challana ??
          0,
      );
      const totalChallanaAmount = Number(
        serverSummary.totalChallanaAmount ??
          serverSummary.total_challana_amount ??
          0,
      );

      setSummary({
        totalRecords: totalRecordsFromApi,
        totalBills: Number(serverSummary.totalBills ?? serverSummary.total_bills ?? normalizedBills.length ?? 0),
        totalCancelled: Number(serverSummary.totalCancelled ?? serverSummary.total_cancelled ?? 0),
        totalRevenue: Number(serverSummary.totalRevenue ?? serverSummary.total_revenue ?? 0),
        totalServiceCharge,
        totalChallanaAmount,
        pendingAmount: Number(serverSummary.pendingAmount ?? serverSummary.pending_amount ?? 0),
        discountAmount: Number(serverSummary.discountAmount ?? serverSummary.discount_amount ?? 0),
        couponDiscount: Number(serverSummary.couponDiscount ?? serverSummary.coupon_discount ?? 0),
        statusCounts,
      });
    } catch (err) {
      console.error('❌ Failed to load bills:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      });
      const message = err.message || err.response?.data?.message || 'Failed to load bills. Please check your connection and try again.';
      setError(message);
      toast.error(message);
      setBills([]);
      setSummary({
        totalRecords: 0,
        totalBills: 0,
        totalCancelled: 0,
        totalRevenue: 0,
        totalServiceCharge: 0,
        totalChallanaAmount: 0,
        pendingAmount: 0,
        discountAmount: 0,
        couponDiscount: 0,
        statusCounts: {
          under_processing: 0,
          pending: 0,
          completed: 0,
          cancelled: 0,
        },
      });
      setPagination({ page: pageNum, pages: 1, total: 0, limit: pageSize });
    } finally {
      setLoading(false);
    }
  }, [advanceMode, currentPage, fromDate, pageSize, quickRange, searchTerm, statusFilter, workStatusFilter, toDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, workStatusFilter, pageSize, quickRange, fromDate, toDate]);

  useEffect(() => {
    fetchBills();
  }, [currentPage, fetchBills]);

  // Auto-scroll modal to top when it opens
  useEffect(() => {
    if (editBill) {
      const scrollableElement = document.querySelector('.edit-modal-content-scrollable');
      if (scrollableElement) {
        setTimeout(() => {
          scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
      prevWorkStatusRef.current = null;
    }
  }, [editBill]);

  // Focus the pending remark box when Pending is selected (ready to type)
  useEffect(() => {
    const status = editForm?.workStatus;
    if (status === 'pending' && prevWorkStatusRef.current !== 'pending') {
      const focusPendingRemark = () => {
        const el = pendingRemarkRef.current;
        if (!el) return;
        el.focus({ preventScroll: true });
        const len = el.value.length;
        el.setSelectionRange(len, len);
        el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      };
      // Wait for the pending panel to mount
      const raf = requestAnimationFrame(() => {
        setTimeout(focusPendingRemark, 50);
      });
      prevWorkStatusRef.current = status;
      return () => cancelAnimationFrame(raf);
    }
    prevWorkStatusRef.current = status ?? null;
  }, [editForm?.workStatus]);

  const sortedBills = useMemo(() => {
    const cloned = [...bills];
    switch (sortOption) {
      case 'date_asc':
        return cloned.sort((a, b) => new Date(a.billDate || 0) - new Date(b.billDate || 0));
      case 'date_desc':
        return cloned.sort((a, b) => new Date(b.billDate || 0) - new Date(a.billDate || 0));
      case 'service_charge_asc':
        return cloned.sort((a, b) => getTotalServiceCharge(a) - getTotalServiceCharge(b));
      case 'service_charge_desc':
        return cloned.sort((a, b) => getTotalServiceCharge(b) - getTotalServiceCharge(a));
      case 'challana_asc':
        return cloned.sort((a, b) => getTotalChallanaAmount(a) - getTotalChallanaAmount(b));
      case 'challana_desc':
        return cloned.sort((a, b) => getTotalChallanaAmount(b) - getTotalChallanaAmount(a));
      case 'paid_asc':
        return cloned.sort((a, b) => (a.paidAmount || 0) - (b.paidAmount || 0));
      case 'paid_desc':
        return cloned.sort((a, b) => (b.paidAmount || 0) - (a.paidAmount || 0));
      case 'due_asc':
        return cloned.sort((a, b) => (a.dueAmount || 0) - (b.dueAmount || 0));
      case 'due_desc':
        return cloned.sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0));
      case 'amount_asc':
        return cloned.sort((a, b) => (a.totalAmount || 0) - (b.totalAmount || 0));
      case 'amount_desc':
        return cloned.sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0));
      case 'name_desc':
        return cloned.sort((a, b) => (b.customerName || '').localeCompare(a.customerName || ''));
      case 'status':
        return cloned.sort((a, b) => {
          const statusOrder = { paid: 0, partial: 1, due: 2 };
          return (statusOrder[a.paymentStatus] || 3) - (statusOrder[b.paymentStatus] || 3);
        });
      case 'name_asc':
      default:
        return cloned.sort((a, b) => (a.customerName || '').localeCompare(b.customerName || ''));
    }
  }, [bills, sortOption]);

  const visibleBills = sortedBills;

  // Clear selections when bills change or filters are applied
  useEffect(() => {
    setSelectedBills((prev) => prev.filter((id) => visibleBills.some((bill) => bill.id === id)));
  }, [visibleBills]);
  const totalPages = Math.max(1, pagination.pages || 1);
  const currentPageSafe = Math.min(pagination.page || currentPage, totalPages);
  const normalizedSearchPhone = searchTerm.replace(/\D/g, '').slice(0, 10);

  const filteredBillCount = summary.totalRecords || pagination.total || 0;
  const filteredServiceAmount = summary.totalServiceCharge || 0;
  const filteredChallanaAmount = summary.totalChallanaAmount || 0;
  const filteredBillAmount = summary.totalRevenue || 0;
  const filterStatsHint = searchTerm.trim()
    ? `Totals for bills matching “${searchTerm.trim()}” (service amount counts matching service lines only).`
    : `Totals for ${selectedRangeLabel.toLowerCase()}${quickRange === 'custom' && (fromDate || toDate) ? ` (${fromDate || '…'} to ${toDate || '…'})` : ''}.`;

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setWorkStatusFilter('all');
    setSortOption('date_desc');
    setPageSize(10);
    setQuickRange('thisYear');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
    setSelectedBills([]);
  };

  const handleQuickRangeChange = (rangeId) => {
    setQuickRange(rangeId);
  };

  const openWhatsappHistory = () => {
    const cleanPhone = normalizedSearchPhone;
    if (!cleanPhone || cleanPhone.length !== 10) {
      toast.warn('Enter a valid 10-digit phone number in the search box to view WhatsApp history.');
      return;
    }
    const countryCode = process.env.REACT_APP_WHATSAPP_COUNTRY_CODE || '91';
    const whatsappUrl = `https://wa.me/${countryCode}${cleanPhone}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEdit = async (bill) => {
    setEditBill(bill);
    setCancelPanelOpen(false);
    setCancelFee(DEFAULT_CANCEL_PROCESSING_FEE);
    setCancelReason('');
    setCancelMethod('cash');
    setCancelBucket('service_charges');
    // Debug: Log bill data to see what we're receiving
    console.log('🔍 Edit Bill - Raw bill data:', {
      id: bill.id,
      billNo: bill.billNo,
      services: bill.services,
      servicesLength: bill.services?.length,
      servicesType: typeof bill.services,
      servicesIsArray: Array.isArray(bill.services),
    });
    
    // Create editable services array - ensure it's always an array
    // Handle both array and non-array cases
    let editableServices = [];
    if (bill.services) {
      if (Array.isArray(bill.services) && bill.services.length > 0) {
        editableServices = bill.services.map((service) => ({
          id: service.id,
          name: service.name || service.service_name || service.display_name || '',
          serviceCharge: Number(service.serviceCharge || service.service_charge || 0),
          challanaAmount: Number(service.challanaAmount || service.challana_amount || 0),
          total: Number(service.total || 0),
          quantity: Number(service.quantity || 1),
        }));
      }
    }
    
    console.log('✅ Edit Bill - Processed services:', editableServices);
    const rawStatus = bill.workStatus || 'completed';
    const editStatus = normalizeEditWorkStatus(rawStatus);
    const sanitizedRemark = sanitizeWorkRemark(bill.workRemark || '');
    const initialWorkRemark =
      editStatus === 'pending'
        ? applyPendingDefaultRemark(sanitizedRemark)
        : sanitizedRemark;
    // Initialize payment methods
    const paymentMethods = bill.paymentMethods || {};
    
    // Fetch customer profession if phone number is available
    let customerProfession = '';
    if (bill.customerPhone) {
      try {
        const customerResponse = await customerApi.getByPhone(bill.customerPhone);
        if (customerResponse.success && customerResponse.data) {
          customerProfession = customerResponse.data.profession || '';
        }
      } catch (err) {
        console.warn('Failed to fetch customer profession:', err);
      }
    }
    
    setEditForm({
      customerName: bill.customerName || '',
      customerPhone: bill.customerPhone || '',
      customerAddress: bill.customerAddress || '',
      customerCity: bill.customerCity || '',
      customerPincode: bill.customerPincode || '',
      profession: customerProfession,
      remarks: bill.remarks || '',
      paidAmount: bill.paidAmount || 0,
      dueAmount: bill.dueAmount || 0,
      workStatus: editStatus,
      workRemark: initialWorkRemark,
      legacyWorkStatus: rawStatus,
      isAdvance: !!bill.isAdvance,
      salesCountedAt: bill.salesCountedAt || null,
      services: editableServices,
      paymentMethods: {
        cash: Number(paymentMethods.cash || 0),
        upi: Number(paymentMethods.upi || 0),
        card: Number(paymentMethods.card || 0),
        aeps:
          Number(paymentMethods.aeps || paymentMethods.netBanking || 0),
      },
      previousPaidAmount: bill.paidAmount || 0, // Store original paid amount
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => {
      if (!prev || !editBill) return prev;
      const updated = { ...prev };

      if (field === 'paidAmount') {
        const paid = Math.max(0, Number(value) || 0);
        const newTotal = updated.services
          ? updated.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
          : Number(editBill.totalAmount) || 0;
        updated.paidAmount = paid;
        updated.dueAmount = Math.max(0, Number((newTotal - paid).toFixed(2)));
      } else if (field === 'dueAmount') {
        const due = Math.max(0, Number(value) || 0);
        const newTotal = updated.services
          ? updated.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
          : Number(editBill.totalAmount) || 0;
        updated.dueAmount = due;
        updated.paidAmount = Math.max(0, Number((newTotal - due).toFixed(2)));
      } else if (field === 'workStatus') {
        updated.workStatus = value;
        if (value === 'completed') {
          updated.workRemark = sanitizeWorkRemark(updated.workRemark);
        } else if (value === 'pending') {
          updated.workRemark = applyPendingDefaultRemark(updated.workRemark);
        }
      } else if (field === 'isAdvance') {
        updated.isAdvance = !!value;
      } else if (field === 'customerPhone') {
        // Clean phone number (only digits, max 10)
        const cleanPhone = value.replace(/\D/g, '').slice(0, 10);
        updated.customerPhone = cleanPhone;
      } else {
        updated[field] = value;
      }

      return updated;
    });
  };

  const handleServiceChange = (serviceIndex, field, value) => {
    setEditForm((prev) => {
      if (!prev || !prev.services || !prev.services[serviceIndex]) return prev;
      const updated = { ...prev };
      // Create a new array to avoid mutation
      const services = updated.services.map((s, idx) => {
        if (idx !== serviceIndex) return s;
        
        // Create a new service object
        const service = { ...s };
        
        const quantity = Number(service.quantity) || 1;

        if (field === 'name') {
          service.name = value;
        } else if (field === 'quantity') {
          const qty = Math.max(1, Number(value) || 1);
          service.quantity = qty;
          service.total = Number(
            (qty * ((Number(service.serviceCharge) || 0) + (Number(service.challanaAmount) || 0))).toFixed(2)
          );
        } else if (field === 'serviceCharge') {
          const charge = Math.max(0, Number(value) || 0);
          service.serviceCharge = charge;
          service.total = Number((quantity * (charge + (Number(service.challanaAmount) || 0))).toFixed(2));
        } else if (field === 'challanaAmount') {
          const challan = Math.max(0, Number(value) || 0);
          service.challanaAmount = challan;
          service.total = Number((quantity * ((Number(service.serviceCharge) || 0) + challan)).toFixed(2));
        } else if (field === 'total') {
          service.total = Math.max(0, Number(value) || 0);
        }
        
        return service;
      });
      
      updated.services = services;

      // Recalculate bill total
      const newTotal = services.reduce((sum, s) => sum + (Number(s.total) || 0), 0);
      const currentPaid = Number(updated.paidAmount) || 0;
      updated.dueAmount = Math.max(0, Number((newTotal - currentPaid).toFixed(2)));

      return updated;
    });
  };


  const handlePaymentMethodChange = (method, value) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      const paymentMethods = { ...updated.paymentMethods };
      paymentMethods[method] = Math.max(0, Number(value) || 0);
      
      // Calculate total paid from all payment methods
      const totalPaid = Object.values(paymentMethods).reduce((sum, val) => sum + (Number(val) || 0), 0);
      
      // Calculate new total from services
      const newTotal = updated.services
        ? updated.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
        : Number(editBill.totalAmount) || 0;
      
      updated.paymentMethods = paymentMethods;
      updated.paidAmount = totalPaid;
      updated.dueAmount = Math.max(0, Number((newTotal - totalPaid).toFixed(2)));
      
      return updated;
    });
  };

  const setEditWorkStatus = (status) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, workStatus: status };
      // Completed: never keep boilerplate delivery text
      if (status === 'completed') {
        updated.workRemark = sanitizeWorkRemark(updated.workRemark);
      } else if (status === 'pending') {
        updated.workRemark = applyPendingDefaultRemark(updated.workRemark);
      }
      return updated;
    });
  };

  const applyPendingReason = (reason) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        workStatus: 'pending',
        workRemark: reason,
      };
    });
  };

  const cancelRefundPreview = useMemo(() => {
    if (!editForm) {
      return {
        paidAmount: 0,
        serviceChargeTotal: 0,
        challanaTotal: 0,
        processingFeeRetained: 0,
        refundAmount: 0,
        maxRetainable: 0,
      };
    }
    const paid = Math.max(0, Number(editForm.paidAmount) || 0);
    const serviceChargeTotal = (editForm.services || []).reduce(
      (sum, s) => sum + (Number(s.serviceCharge) || 0) * (Number(s.quantity) || 1),
      0
    );
    const challanaTotal = (editForm.services || []).reduce(
      (sum, s) => sum + (Number(s.challanaAmount) || 0) * (Number(s.quantity) || 1),
      0
    );
    const maxRetainable = Math.min(paid, serviceChargeTotal > 0 ? serviceChargeTotal : paid);
    const fee = Math.min(Math.max(0, Number(cancelFee) || 0), maxRetainable);
    const refund = Math.max(0, paid - fee);
    return {
      paidAmount: paid,
      serviceChargeTotal,
      challanaTotal,
      processingFeeRetained: fee,
      refundAmount: refund,
      maxRetainable,
    };
  }, [editForm, cancelFee]);

  const handleCancelWithRefund = async () => {
    if (!editBill || !editForm) return;
    if (editBill.refundedAt) {
      toast.info('This bill was already cancelled with a refund.');
      return;
    }
    const reason = String(cancelReason || '').trim();
    if (!reason) {
      toast.warn('Pick a reason, then confirm.');
      return;
    }
    const preview = cancelRefundPreview;
    const ok = window.confirm(
      `Cancel this bill?\n\nService charge: ${formatCurrency(preview.serviceChargeTotal || preview.paidAmount)}\nProcessing charge: ${formatCurrency(preview.processingFeeRetained)}\nRefund (deduct from bill date): ${formatCurrency(preview.refundAmount)}`
    );
    if (!ok) return;

    try {
      setCancelBusy(true);
      const response = await billsApi.cancelWithRefund(editBill.id, {
        processing_fee: Number(cancelFee) || 0,
        refund_reason: reason,
        refund_method: cancelMethod,
        refund_bucket: cancelBucket,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Cancel refund failed');
      }
      toast.success(response.message || 'Bill cancelled with refund.');
      setEditBill(null);
      setEditForm(null);
      setCancelPanelOpen(false);
      await fetchBills(currentPage);
    } catch (err) {
      console.error('Cancel refund failed:', err);
      toast.error(err.message || 'Failed to cancel with refund');
    } finally {
      setCancelBusy(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editBill || !editForm) return;

    const workStatus =
      editForm.workStatus === 'cancelled' ? 'cancelled' : editForm.workStatus;
    const workRemark = sanitizeWorkRemark(editForm.workRemark);

    if (workStatus === 'pending' && isPendingRemarkIncomplete(editForm.workRemark)) {
      toast.warn('Add a short remark explaining what is pending before saving.');
      pendingRemarkRef.current?.focus({ preventScroll: true });
      return;
    }

    // Calculate new total from updated services
    const newTotalAmount = editForm.services
      ? editForm.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
      : Number(editBill.totalAmount) || 0;
    const paidAmount = Math.min(newTotalAmount, Math.max(0, Number(editForm.paidAmount) || 0));
    const dueAmount = Math.max(0, Number((newTotalAmount - paidAmount).toFixed(2)));
    const paymentStatus = dueAmount <= 0 ? 'paid' : paidAmount > 0 ? 'partial' : 'due';

    try {
      setLoading(true);
      
      // Format services for backend - ensure all required fields are present
      const formattedServices = editForm.services && Array.isArray(editForm.services)
        ? editForm.services.map((service) => ({
            name: service.name || '',
            serviceCharge: Number(service.serviceCharge || 0),
            challanaAmount: Number(service.challanaAmount || 0),
            total: Number(service.total || 0),
            quantity: Number(service.quantity || 1),
            // Optional fields
            subService: service.subService || '',
            displayName: service.displayName || service.name || '',
            hasDynamicSubService: service.hasDynamicSubService || false,
            hasDynamicPrice: service.hasDynamicPrice || false,
          }))
        : [];
      
      // Update bill
      await billsApi.update(editBill.id, {
        customer_name: editForm.customerName,
        customer_phone: editForm.customerPhone,
        customer_address: editForm.customerAddress || '',
        customer_city: editForm.customerCity || '',
        customer_pincode: editForm.customerPincode || '',
        remarks: editForm.remarks,
        payment_status: paymentStatus,
        paid_amount: paidAmount,
        due_amount: dueAmount,
        total_amount: newTotalAmount,
        payment_methods: editForm.paymentMethods || editBill.paymentMethods,
        work_status: workStatus,
        work_remark: workStatus === 'completed' ? (workRemark || '') : workRemark,
        is_advance: !!editForm.isAdvance,
        isAdvance: !!editForm.isAdvance,
        services: formattedServices,
      });
      
      // Update customer profession if phone number is available
      if (editForm.customerPhone && editForm.profession !== undefined) {
        try {
          await customerApi.save({
            phone: editForm.customerPhone,
            name: editForm.customerName,
            profession: editForm.profession || '',
            address: editForm.customerAddress || '',
            city: editForm.customerCity || '',
            pincode: editForm.customerPincode || '',
          });
        } catch (customerErr) {
          console.warn('Failed to update customer profession:', customerErr);
          // Don't fail the entire operation if customer update fails
        }
      }
      
      toast.success('Bill updated successfully.');
      setEditBill(null);
      setEditForm(null);
      await fetchBills(currentPage);
    } catch (err) {
      console.error('Failed to update bill:', err);
      toast.error(`Failed to update bill: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRazorpayPayBillDue = async () => {
    if (!editBill || !editForm) return;
    const due = Number(editForm.dueAmount) || 0;
    if (due <= 0) {
      toast.info('There is no balance due for online payment.');
      return;
    }
    const paise = Math.round(due * 100);
    if (!Number.isFinite(paise) || paise < 100) {
      toast.error('Online payment must be at least ₹1.');
      return;
    }

    setRazorpayBusy(true);
    try {
      const orderRes = await paymentsApi.createRazorpayOrder({
        amount: paise,
        currency: 'INR',
        receipt: `sb_${String(editBill.id).replace(/[^a-zA-Z0-9_]/g, '_')}`.slice(0, 39),
        billId: editBill.id,
      });
      if (!orderRes?.success || !orderRes.order || !orderRes.keyId) {
        toast.error(orderRes?.message || 'Razorpay is not available. Check server configuration.');
        return;
      }

      const newTotalAmount = editForm.services
        ? editForm.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
        : Number(editBill.totalAmount) || 0;
      const billLabel = editBill.billNo || editBill.id;

      await openRazorpayCheckout({
        order: orderRes.order,
        keyId: orderRes.keyId,
        customer: {
          name: editForm.customerName,
          phone: editForm.customerPhone,
        },
        description: `Bill ${billLabel} — ₹${due.toFixed(2)}`,
        onSuccess: async (rzResponse) => {
          setRazorpayBusy(true);
          try {
            const verifyRes = await paymentsApi.verifyRazorpayPayment({
              razorpay_order_id: rzResponse.razorpay_order_id,
              razorpay_payment_id: rzResponse.razorpay_payment_id,
              razorpay_signature: rzResponse.razorpay_signature,
            });
            if (!verifyRes?.success && !verifyRes?.verified) {
              toast.error('Payment could not be verified. Check Razorpay dashboard before editing manually.');
              return;
            }

            const paidRupeeFromGateway = paise / 100;
            const newMethods = {
              ...editForm.paymentMethods,
              upi: Number(
                (Number(editForm.paymentMethods?.upi || 0) + paidRupeeFromGateway).toFixed(2),
              ),
            };
            const totalPaidFromMethods = Object.values(newMethods).reduce(
              (sum, val) => sum + (Number(val) || 0),
              0,
            );
            const paidAmount = Math.min(newTotalAmount, Number(totalPaidFromMethods.toFixed(2)));
            const dueAmount = Math.max(0, Number((newTotalAmount - paidAmount).toFixed(2)));
            const paymentStatus =
              dueAmount <= 0 ? 'paid' : paidAmount > 0 ? 'partial' : 'due';

            const formattedServices =
              editForm.services && Array.isArray(editForm.services)
                ? editForm.services.map((service) => ({
                    name: service.name || '',
                    serviceCharge: Number(service.serviceCharge || 0),
                    challanaAmount: Number(service.challanaAmount || 0),
                    total: Number(service.total || 0),
                    quantity: Number(service.quantity || 1),
                    subService: service.subService || '',
                    displayName: service.displayName || service.name || '',
                    hasDynamicSubService: service.hasDynamicSubService || false,
                    hasDynamicPrice: service.hasDynamicPrice || false,
                  }))
                : [];

            await billsApi.update(editBill.id, {
              customer_name: editForm.customerName,
              customer_phone: editForm.customerPhone,
              customer_address: editForm.customerAddress || '',
              customer_city: editForm.customerCity || '',
              customer_pincode: editForm.customerPincode || '',
              remarks: editForm.remarks,
              payment_status: paymentStatus,
              paid_amount: paidAmount,
              due_amount: dueAmount,
              total_amount: newTotalAmount,
              payment_methods: newMethods,
              work_status: editForm.workStatus,
              work_remark: editForm.workRemark,
              services: formattedServices,
            });

            setEditForm((prev) =>
              prev
                ? {
                    ...prev,
                    paymentMethods: newMethods,
                    paidAmount,
                    dueAmount,
                  }
                : prev,
            );

            toast.success('Razorpay payment recorded on this bill.');
            await fetchBills(currentPage);
          } catch (err) {
            console.error('Razorpay bill payment error:', err);
            toast.error(err?.message || 'Failed to save payment on the bill.');
          } finally {
            setRazorpayBusy(false);
          }
        },
      });
    } catch (err) {
      console.error('Razorpay order error:', err);
      toast.error(err?.message || err?.error || 'Could not start Razorpay checkout.');
    } finally {
      setRazorpayBusy(false);
    }
  };

  const handleBillTypeChange = async (bill, targetType) => {
    if (!bill || !targetType || bill.billType === targetType) return;
    try {
      setBillTypeUpdatingId(bill.id);
      await billsApi.update(bill.id, { bill_type: targetType });
      setBills((prevBills) =>
        prevBills.map((item) => (item.id === bill.id ? { ...item, billType: targetType } : item))
      );
      toast.success(
        targetType === 'coupon' ? 'Bill converted to Coupon Bill.' : 'Bill reverted to Regular Bill.'
      );
    } catch (err) {
      console.error('Failed to update bill type:', err);
      toast.error(err.message || 'Failed to update bill type. Please try again.');
    } finally {
      setBillTypeUpdatingId(null);
    }
  };

  const handleCall = (bill) => {
    if (!bill.customerPhone) {
      toast.error('Customer phone number is required to make a call');
      return;
    }

    const phone = bill.customerPhone.replace(/\D/g, '').slice(0, 10);
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    const countryCode = process.env.REACT_APP_WHATSAPP_COUNTRY_CODE || '91';
    const telUrl = `tel:+${countryCode}${phone}`;
    window.location.href = telUrl;
  };

  const handleOpenEnquiry = (bill) => {
    const workPurpose = buildEnquiryWorkPurpose(bill);
    if (!workPurpose) {
      toast.error('No service on this bill to use as enquiry subject');
      return;
    }

    const phone = String(bill.customerPhone || '').replace(/\D/g, '').slice(-10);
    const billRef = bill.billNo || bill.id || '';
    navigate('/bills/enquiry-form', {
      state: {
        prefill: {
          person_name: bill.customerName || '',
          phone: phone.length === 10 ? phone : '',
          work_purpose: workPurpose,
          remarks: billRef ? `Follow-up from bill ${billRef}` : '',
          bill_data: {
            channel: 'view_bills_enquiry',
            linked_bill_id: bill.id,
            ...(bill.billNo ? { linked_bill_no: bill.billNo } : {}),
          },
        },
      },
    });
  };

  const handleShareCoupons = (bill) => {
    if (!bill.customerPhone) {
      toast.error('Customer phone number is required to share coupons');
      return;
    }

    const phone = bill.customerPhone.replace(/\D/g, '').slice(0, 10);
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    const loginUrl = buildCustomerLoginUrl(phone);
    if (!loginUrl) {
      toast.error('Could not build coupon login link');
      return;
    }

    const name = bill.customerName || 'Customer';
    const message =
      `Hi ${name}!\n` +
      `Your LOCSY / KNLS coupons are ready.\n\n` +
      `🔓 Auto-login coupons link:\n${loginUrl}\n\n` +
      `One-click login → see your coupons → Redeem.\n` +
      `Password tip: last 4 digits of your mobile number.`;

    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    const whatsappWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!whatsappWindow || whatsappWindow.closed || typeof whatsappWindow.closed === 'undefined') {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(message).then(() => {
          toast.success('Coupon link copied — paste in WhatsApp.');
        }).catch(() => {
          window.location.href = whatsappUrl;
        });
      } else {
        window.location.href = whatsappUrl;
      }
    } else {
      toast.success('Opening WhatsApp with coupon link...');
    }
  };

  /** Additive print option — cash-credit HTML invoice; does not alter bill data. */
  const handlePrintBill = async (bill) => {
    try {
      await openCashCreditBillPrint(bill);
      toast.success('Printable HTML bill opened — use Print Bill in the new window.');
    } catch (err) {
      console.error('Print bill failed:', err);
      toast.error(err?.message || 'Could not open print bill');
    }
  };

  /** Open HTML invoice + download file + WhatsApp to customer number. */
  const handleSendPrintBill = async (bill) => {
    try {
      const result = await openCashCreditBillPrintAndSend(bill);
      toast.success(
        result.htmlOpened
          ? `HTML bill opened + downloaded (${result.filename}). WhatsApp ready for ${result.phone} — attach the HTML file in chat.`
          : `HTML downloaded (${result.filename}). WhatsApp ready — attach the file in chat.`,
      );
    } catch (err) {
      console.error('Send print bill failed:', err);
      toast.error(err?.message || 'Could not send print bill');
    }
  };

  const handleWhatsAppShare = (bill) => {
    if (!bill.customerPhone) {
      toast.error('Customer phone number is required to share via WhatsApp');
      return;
    }

    const phone = bill.customerPhone.replace(/\D/g, '').slice(0, 10);
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // Format bill details for WhatsApp message with proper line breaks and emojis
    const servicesList = (bill.services || [])
      .map((service, index) => `${index + 1}. 🎯 *${service.name}*\n   📊 Qty: ${service.quantity || 1} | 💰 Amount: ₹${formatNumber(service.total || service.price || 0)}`)
      .join('\n\n');

    const workDisplay = getWorkStatusDisplay(bill);
    const workStatusLabel = workDisplay.label;
    const paymentStatusLabel = bill.paymentStatus?.toUpperCase() || 'PENDING';
    
    // Get status emoji
    const paymentEmoji = bill.paymentStatus === 'paid' ? '✅' : bill.paymentStatus === 'partial' ? '⚠️' : '⏳';
    const workEmoji = bill.isAdvance
      ? '💰'
      : bill.workStatus === 'completed'
        ? '✅'
        : bill.workStatus === 'pending'
          ? '⏳'
          : bill.workStatus === 'under_processing'
            ? '⚙️'
            : '❌';

    // Build message with proper formatting and emojis
    let message = `📋 *BILL DETAILS*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `🆔 *Bill No:* ${bill.billNo}\n\n`;
    message += `👤 *Customer Name:*\n${bill.customerName || 'N/A'}\n\n`;
    message += `📞 *Phone Number:*\n${bill.customerPhone}\n\n`;
    message += `📅 *Bill Date:*\n${formatBillDate(bill.billDate)}\n\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `📦 *SERVICES USED:*\n\n`;
    message += `${servicesList || 'N/A'}\n\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `💰 *PAYMENT SUMMARY:*\n\n`;
    message += `💵 Total Amount: ₹${formatNumber(bill.totalAmount || 0)}\n`;
    message += `✅ Paid Amount: ₹${formatNumber(bill.paidAmount || 0)}\n`;
    message += `${bill.dueAmount > 0 ? `⏳ Due Amount: ₹${formatNumber(bill.dueAmount || 0)}\n` : ''}`;
    message += `\n${paymentEmoji} *Payment Status:* ${paymentStatusLabel}\n\n`;
    message += `⚙️ *WORK STATUS:*\n\n`;
    message += `${workEmoji} *Status:* ${workStatusLabel}\n`;
    if (bill.workRemark) {
      message += `📝 *Remarks:* ${bill.workRemark}\n`;
    }

    const loginUrl = buildCustomerLoginUrl(phone);
    const defaultPassword = phone.slice(-4);
    message += `\n━━━━━━━━━━━━━━━━━━━━\n\n`;
    if (loginUrl) {
      message += `🔓 *YOUR LOGIN LINK:*\n\n`;
      message += `${loginUrl}\n\n`;
      message += `👤 *Login ID:* ${phone}\n`;
      message += `🔑 *Password:* ${defaultPassword}\n\n`;
      message += `One-click login → see your coupons → Redeem.\n`;
      message += `Password tip: last 4 digits of your mobile number.\n\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    }
    message += `🙏 *Thank you for your business!*\n\n`;
    message += `💼 *KNLS - Online Services*\n`;
    message += `📍 Main Road, Chintalapudi\n`;
    message += `📱 For queries: 9885750476`;

    // Create WhatsApp URL with properly encoded message
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodedMessage}`;
    
    // Try to open WhatsApp
    const whatsappWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    
    // Fallback: Copy to clipboard if window doesn't open
    if (!whatsappWindow || whatsappWindow.closed || typeof whatsappWindow.closed === 'undefined') {
      // Copy message to clipboard as fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(message).then(() => {
          toast.success('Message copied to clipboard! Paste it in WhatsApp manually.');
        }).catch(() => {
          toast.info('Opening WhatsApp... If message doesn\'t paste automatically, please copy it manually.');
          window.location.href = whatsappUrl;
        });
      } else {
        toast.info('Opening WhatsApp... If message doesn\'t paste automatically, please copy it manually.');
        window.location.href = whatsappUrl;
      }
    } else {
      toast.success('Opening WhatsApp...');
    }
  };

  const handleDelete = async (billId) => {
    if (!window.confirm('Delete this bill record? This action cannot be undone.')) {
      return;
    }

    const nextPage = bills.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;

    try {
      setLoading(true);
      await billsApi.remove(billId);
      toast.success('Bill deleted successfully.');
      // Remove from selected bills if it was selected
      setSelectedBills((prev) => prev.filter((id) => id !== billId));
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await fetchBills(nextPage);
      }
    } catch (err) {
      console.error('Failed to delete bill:', err);
      toast.error(`Failed to delete bill: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBill = (billId) => {
    setSelectedBills((prev) => {
      if (prev.includes(billId)) {
        return prev.filter((id) => id !== billId);
      } else {
        return [...prev, billId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedBills.length === visibleBills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(visibleBills.map((bill) => bill.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBills.length === 0) {
      toast.warn('Please select at least one bill to delete.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedBills.length} bill record(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Delete bills one by one
      for (const billId of selectedBills) {
        try {
          await billsApi.remove(billId);
          successCount++;
        } catch (err) {
          console.error(`Failed to delete bill ${billId}:`, err);
          failCount++;
        }
      }

      // Clear selections
      setSelectedBills([]);

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} bill record(s).`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} bill record(s).`);
      }

      // Refresh the bills list
      const nextPage = bills.length <= selectedBills.length && currentPage > 1 ? currentPage - 1 : currentPage;
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await fetchBills(nextPage);
      }
    } catch (err) {
      console.error('Failed to delete bills:', err);
      toast.error(`Failed to delete bills: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!sortedBills.length) {
      toast.info('No bill data available to export.');
      return;
    }
    const header = ['Bill No', 'Date', 'Customer Name', 'Customer Phone', 'Service Charge', 'Challana Amount', 'Total Amount', 'Paid Amount', 'Due Amount', 'Status'];
    const rows = sortedBills.map((bill) => [
      bill.billNo,
      bill.billDate
        ? parseBillTimestamp(bill.billDate)?.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) || ''
        : '',
      bill.customerName,
      bill.customerPhone,
      getTotalServiceCharge(bill),
      getTotalChallanaAmount(bill),
      bill.totalAmount,
      bill.paidAmount,
      bill.dueAmount,
      bill.paymentStatus,
    ]);
    const csvContent = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `locsy-bills-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Bills exported as CSV.');
  };

  const renderStatusBadge = (bill) => {
    const status = bill.paymentStatus;
    const className = `status-badge status-${status}`;
    const label =
      status === 'partial' ? 'Partial' : status === 'paid' ? 'Paid' : status === 'due' ? 'Due' : status === 'unpaid' ? 'Unpaid' : status;
    return <span className={className}>{label}</span>;
  };

  return (
    <div className="view-bills-page">
      <header className="view-bills-header">
        <div className="header-content">
          <div>
            <h1>{advanceMode ? 'Advance Bills' : 'View Bills'}</h1>
            <p>
              {advanceMode
                ? 'Advance amount bills — count in counter sales only after work is Completed.'
                : 'Monitor, search, and refine smart bills in real time.'}
            </p>
            {typeof window !== 'undefined' && window.location.hostname.includes('.ondigitalocean') && (
              <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                🔗 API Status: {bills && bills.length > 0 ? '✅ Connected' : '⚠️ Checking...'} | 
                Querying bills from configured backend
              </p>
            )}
          </div>
          <div className="header-actions">
            <button
              className="btn btn-whatsapp"
              type="button"
              onClick={openWhatsappHistory}
              disabled={!normalizedSearchPhone || normalizedSearchPhone.length !== 10}
            >
              <i className="fab fa-whatsapp"></i> WhatsApp History
            </button>
            <Link to="/bills/create" className="btn btn-primary">
              <i className="fas fa-plus-circle"></i> Create New Bill
            </Link>
            <button className="btn btn-secondary" onClick={() => fetchBills(currentPage)}>
              <i className="fas fa-sync"></i> Refresh
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      <section className="status-summary">
        {WORK_STATUS_OPTIONS.map((option) => (
          <div key={option.value} className={`status-chip status-${option.value}`}>
            <span className="status-chip-label">{option.label}</span>
            <span className="status-chip-count">{formatNumber(summary.statusCounts?.[option.value] || 0)}</span>
          </div>
        ))}
      </section>

      <section className="filter-stats" aria-live="polite">
        <div className="filter-stat-card filter-stat-count">
          <span className="filter-stat-label">Total Bills</span>
          <span className="filter-stat-value">{formatNumber(filteredBillCount)}</span>
        </div>
        <button
          type="button"
          className={`filter-stat-card filter-stat-amount ${showServiceAmount ? 'is-expanded' : 'is-collapsed'}`}
          onClick={() => setShowServiceAmount((open) => !open)}
          aria-expanded={showServiceAmount}
          title={showServiceAmount ? 'Hide total service amount' : 'Show total service amount'}
        >
          <span className="filter-stat-label">
            Total Service Amount
            <i className={`fas ${showServiceAmount ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
          </span>
          <span className="filter-stat-value">
            {showServiceAmount ? formatCurrency(filteredServiceAmount) : '••••••'}
          </span>
          {!showServiceAmount && (
            <span className="filter-stat-reveal">Tap to view</span>
          )}
        </button>
        <button
          type="button"
          className={`filter-stat-card filter-stat-amount filter-stat-challana-amount ${showChallanaAmount ? 'is-expanded' : 'is-collapsed'}`}
          onClick={() => setShowChallanaAmount((open) => !open)}
          aria-expanded={showChallanaAmount}
          title={showChallanaAmount ? 'Hide total challana amount' : 'Show total challana amount'}
        >
          <span className="filter-stat-label">
            Total Challana Amount
            <i className={`fas ${showChallanaAmount ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
          </span>
          <span className="filter-stat-value">
            {showChallanaAmount ? formatCurrency(filteredChallanaAmount) : '••••••'}
          </span>
          {!showChallanaAmount && (
            <span className="filter-stat-reveal">Tap to view</span>
          )}
        </button>
        <button
          type="button"
          className={`filter-stat-card filter-stat-amount filter-stat-bill-amount ${showBillAmount ? 'is-expanded' : 'is-collapsed'}`}
          onClick={() => setShowBillAmount((open) => !open)}
          aria-expanded={showBillAmount}
          title={showBillAmount ? 'Hide total bill amount' : 'Show total bill amount'}
        >
          <span className="filter-stat-label">
            Total Bill Amount
            <i className={`fas ${showBillAmount ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
          </span>
          <span className="filter-stat-value">
            {showBillAmount ? formatCurrency(filteredBillAmount) : '••••••'}
          </span>
          {!showBillAmount && (
            <span className="filter-stat-reveal">Tap to view</span>
          )}
        </button>
        <p className="filter-stats-hint">{filterStatsHint}</p>
      </section>

      <section className="view-bills-range">
        <div className="range-header">
          <span>Date Range</span>
          <span className="active-range-label">{selectedRangeLabel}</span>
        </div>
        <div className="quick-range-chips">
          {QUICK_RANGES.map((range) => (
            <button
              key={range.id}
              className={`range-chip ${quickRange === range.id ? 'active' : ''}`}
              onClick={() => handleQuickRangeChange(range.id)}
              type="button"
            >
              {range.label}
            </button>
          ))}
        </div>
        {quickRange === 'custom' && (
          <div className="custom-range-panel">
            <div className="form-field">
              <label htmlFor="from-date">From</label>
              <input id="from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="to-date">To</label>
              <input id="to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                }}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="view-bills-controls">
        <div className="control-group">
          <label htmlFor="bill-search">Search</label>
          <input
            id="bill-search"
            type="search"
            placeholder="Search by customer, phone, bill no, service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="control-group">
          <label htmlFor="bill-status">Payment Status</label>
          <select id="bill-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="bill-work-status">Work Status</label>
          <select id="bill-work-status" value={workStatusFilter} onChange={(e) => setWorkStatusFilter(e.target.value)}>
            <option value="all">All</option>
            {WORK_STATUS_OPTIONS.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="bill-sort">Sort</label>
          <select id="bill-sort" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="bill-page-size">Page Size</label>
          <select id="bill-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="control-buttons">
          {selectedBills.length > 0 && (
            <button className="btn btn-danger" onClick={handleBulkDelete} disabled={loading}>
              <i className="fas fa-trash-alt"></i> Delete Selected ({selectedBills.length})
            </button>
          )}
          <button className="btn btn-outline" onClick={resetFilters}>
            <i className="fas fa-undo"></i> Reset
          </button>
          <button className="btn btn-outline" onClick={handleExport}>
            <i className="fas fa-file-export"></i> Export CSV
          </button>
        </div>
      </section>

      <section className="view-bills-table-wrapper">
        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading bills...</p>
          </div>
        ) : visibleBills.length === 0 ? (
          <div className="empty-state">
            <h3>No bills found</h3>
            <p>Adjust the filters above or create a new bill from the Smart Bill page.</p>
            <Link to="/bills/create" className="btn btn-primary">
              Go to Smart Bill
            </Link>
          </div>
        ) : (
          <table className="view-bills-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={visibleBills.length > 0 && selectedBills.length === visibleBills.length}
                    onChange={handleSelectAll}
                    title="Select All"
                  />
                </th>
                <th>Bill No</th>
                <th>Bill Type</th>
                <th>Customer</th>
                <th>Created</th>
                <th>Created By</th>
                <th>Primary Service</th>
                <th>Service Charge</th>
                <th>Challana Amount</th>
                <th>Total Bill Amount</th>
                <th>Paid</th>
                <th>Due</th>
                <th>Status</th>
                <th>Work Status</th>
                <th>History</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleBills.map((bill) => {
                const primaryService = getPrimaryServiceInfo(bill);
                const isBillTypeUpdating = billTypeUpdatingId === bill.id;
                return (
                <React.Fragment key={bill.id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedBills.includes(bill.id)}
                        onChange={() => handleSelectBill(bill.id)}
                        title="Select this bill"
                      />
                    </td>
                    <td>
                      <button className="bill-link" onClick={() => setDetailBillId((prev) => (prev === bill.id ? null : bill.id))}>
                        {bill.billNo}
                      </button>
                      <span className={`bill-origin-chip ${bill.billOrigin === 'order' ? 'order' : 'counter'}`}>
                        {bill.billOriginLabel || (bill.billOrigin === 'order' ? 'Order bill' : 'Counter bill')}
                      </span>
                    </td>
                      <td>
                        <div
                          className={`bill-type-indicator${isBillTypeUpdating ? ' updating' : ''}`}
                          title={bill.billType === 'coupon' ? 'Coupon Bill' : 'Regular Bill'}
                        >
                          <label className={`bill-type-option coupon${bill.billType === 'coupon' ? ' active' : ''}`}>
                            <input
                              type="radio"
                              name={`bill-type-${bill.id}`}
                              value="coupon"
                              checked={bill.billType === 'coupon'}
                              disabled={isBillTypeUpdating}
                              onChange={() => handleBillTypeChange(bill, 'coupon')}
                            />
                            <span>Coupon Bill</span>
                          </label>
                          <label className={`bill-type-option regular${bill.billType === 'regular' ? ' active' : ''}`}>
                            <input
                              type="radio"
                              name={`bill-type-${bill.id}`}
                              value="regular"
                              checked={bill.billType === 'regular'}
                              disabled={isBillTypeUpdating}
                              onChange={() => handleBillTypeChange(bill, 'regular')}
                            />
                            <span>Regular Bill</span>
                          </label>
                          {isBillTypeUpdating && <span className="bill-type-status">Updating...</span>}
                        </div>
                      </td>
                    <td>
                      <div className="customer-name">{bill.customerName || 'Unknown'}</div>
                      <button 
                        className="btn-ghost customer-phone-btn"
                        onClick={() => navigate(`/bills/create?phone=${bill.customerPhone.replace(/\D/g, '')}&name=${encodeURIComponent(bill.customerName || 'Customer')}&address=${encodeURIComponent(bill.customerAddress || '')}&profession=${encodeURIComponent(bill.customerCity || '')}`)}
                        title="Create new bill for this customer"
                      >
                        📱 {bill.customerPhone}
                      </button>
                    </td>
                    <td className="created-date">{formatDateTimeWithSeconds(bill.createdAt)}</td>
                    <td>{bill.createdByStamp || '—'}</td>
                      <td>
                        <div className={`primary-service${primaryService.isCoupon ? ' coupon' : ''}`}>
                          <span>{primaryService.label}</span>
                          {primaryService.extraCount > 0 && (
                            <span className="service-count-pill">+{primaryService.extraCount}</span>
                          )}
                        </div>
                      </td>
                    <td>{formatCurrency(getTotalServiceCharge(bill))}</td>
                    <td className="challana-amount">{formatCurrency(getTotalChallanaAmount(bill))}</td>
                    <td className="total-bill-amount">{formatCurrency(bill.totalAmount)}</td>
                    <td>{formatCurrency(bill.paidAmount)}</td>
                    <td className={bill.dueAmount > 0 ? 'due-amount' : ''}>{formatCurrency(bill.dueAmount)}</td>
                    <td>{renderStatusBadge(bill)}</td>
                    <td>
                      <span className={`work-status-tag status-${getWorkStatusDisplay(bill).key}`}>
                        {getWorkStatusDisplay(bill).label}
                      </span>
                      {bill.workRemark && <div className="work-remark">{bill.workRemark}</div>}
                    </td>
                    <td>
                      {bill.customerPhone ? (
                        <Link
                          to={`/customer/dashboard?phone=${bill.customerPhone.replace(/\D/g, '')}&name=${encodeURIComponent(bill.customerName || 'Customer')}`}
                            className={`btn-ghost history-link${bill.workStatus === 'pending' ? ' pending' : ''}`}
                          title="View Customer History"
                        >
                          <i className="fas fa-history"></i> History
                        </Link>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button 
                          className="btn-ghost new-bill-button" 
                          onClick={() => navigate(`/bills/create?phone=${bill.customerPhone.replace(/\D/g, '')}&name=${encodeURIComponent(bill.customerName || 'Customer')}&address=${encodeURIComponent(bill.customerAddress || '')}&profession=${encodeURIComponent(bill.customerCity || '')}`)}
                          title="Create new bill for this customer"
                        >
                          <i className="fas fa-plus"></i> New Bill
                        </button>
                        <button 
                          className="btn-ghost call-button" 
                          onClick={() => handleCall(bill)}
                          title="Call customer"
                        >
                          <i className="fas fa-phone"></i> Call
                        </button>
                        <button 
                          className="btn-ghost whatsapp-share" 
                          onClick={() => handleWhatsAppShare(bill)}
                          title="Share via WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i> Share
                        </button>
                        <button
                          className="btn-ghost"
                          onClick={() => handlePrintBill(bill)}
                          title="Open printable HTML cash-credit bill"
                        >
                          <i className="fas fa-print"></i> Print Bill
                        </button>
                        <button
                          className="btn-ghost whatsapp-share"
                          onClick={() => handleSendPrintBill(bill)}
                          title="Open HTML bill, download file, and WhatsApp to customer number"
                        >
                          <i className="fab fa-whatsapp"></i> Send HTML Bill
                        </button>
                        <button
                          className="btn-ghost whatsapp-share"
                          onClick={() => handleShareCoupons(bill)}
                          title="Share auto-login coupons link"
                        >
                          <i className="fas fa-ticket-alt"></i> Share Coupons
                        </button>
                        <button
                          className="btn-ghost"
                          onClick={() => handleOpenEnquiry(bill)}
                          title="Register follow-up enquiry with this bill’s service"
                          disabled={!buildEnquiryWorkPurpose(bill)}
                        >
                          <i className="fas fa-headset"></i> Enquiry
                        </button>
                        <button className="btn-ghost" onClick={() => handleEdit(bill)}>
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button className="btn-ghost danger" onClick={() => handleDelete(bill.id)}>
                          <i className="fas fa-trash-alt"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {detailBillId === bill.id && (
                    <tr className="bill-details-row">
                        <td colSpan={TABLE_COLUMN_COUNT + 1}>
                        <div className="bill-details">
                          <div>
                            <h4>Services</h4>
                            <ul>
                              {(bill.services || []).map((service) => (
                                <li key={`${bill.id}-${service.id || service.name}`}>
                                  <span>{service.name}</span>
                                  <span>
                                    Qty: {service.quantity} • {formatCurrency(service.total)}
                                    {Number(service.challanaAmount) > 0
                                      ? ` • Challana ${formatCurrency(
                                          Number(service.challanaAmount) * (Number(service.quantity) || 1),
                                        )}`
                                      : ''}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {bill.offers && bill.offers.length > 0 && (
                            <div>
                              <h4>Offers Applied</h4>
                              <ul>
                                {bill.offers.map((offer) => (
                                  <li key={`${bill.id}-${offer.id || offer.name}`}>
                                    <span>{offer.name}</span>
                                    <span>Saved {formatCurrency(offer.savings)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div>
                            <h4>Payment Methods</h4>
                            <ul>
                              {Object.entries(bill.paymentMethods || {}).map(([method, amount]) => (
                                <li key={`${bill.id}-${method}`}>
                                  <span className="method-label">{method.toUpperCase()}</span>
                                  <span>{formatCurrency(amount)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {bill.remarks && (
                            <div className="remarks">
                              <h4>Remarks</h4>
                              <p>{bill.remarks}</p>
                            </div>
                          )}
                          <div className="work-meta">
                            <div className="work-meta-row">
                              <span className="label">Work Status</span>
                              <span className={`work-status-tag status-${getWorkStatusDisplay(bill).key}`}>
                                {getWorkStatusDisplay(bill).label}
                              </span>
                            </div>
                            {bill.workRemark && (
                              <div className="work-meta-row">
                                <span className="label">Status Remark</span>
                                <span className="value">{bill.workRemark}</span>
                              </div>
                            )}
                          </div>
                          <div className="timestamps">
                            <span>Created: {formatDateTimeWithSeconds(bill.createdAt)}</span>
                            <span>Updated: {formatDateTimeWithSeconds(bill.updatedAt)}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="view-bills-pagination">
        <button
          className="btn btn-outline pagination-btn"
          disabled={currentPageSafe <= 1 || loading}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPageSafe} of {totalPages}
        </span>
        <button
          className="btn btn-outline pagination-btn"
          disabled={currentPageSafe >= totalPages || loading}
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        >
          Next
        </button>
      </section>

      {editBill && editForm && (
        <div className="edit-modal">
          <div className="edit-modal-backdrop" onClick={() => setEditBill(null)}></div>
          <div className="edit-modal-content edit-bill-dialog">
            <div className="edit-modal-header">
              <div className="edit-modal-header-text">
                <h3>Edit Bill</h3>
                {editBill?.billNo && (
                  <p className="edit-modal-subtitle">
                    {editBill.billNo}
                    <span aria-hidden="true"> · </span>
                    <span className="edit-modal-service">{editServiceSummary}</span>
                  </p>
                )}
              </div>
              <button className="edit-modal-close" onClick={() => setEditBill(null)} aria-label="Close">
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="edit-modal-content-scrollable">
            {/* Services Section - Always show */}
            <div className="edit-services-section">
              <h4 className="section-title">Services</h4>
              {editForm.services && Array.isArray(editForm.services) && editForm.services.length > 0 ? (
                <div className="edit-services-table-wrapper">
                  <table className="edit-services-table">
                    <colgroup>
                      <col className="col-service-name" />
                      <col className="col-service-qty" />
                      <col className="col-service-charge" />
                      <col className="col-service-challan" />
                      <col className="col-service-total" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>SERVICE NAME</th>
                        <th>QTY</th>
                        <th>SERVICE CHARGE</th>
                        <th>CHALLAN</th>
                        <th>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editForm.services.map((service, index) => (
                        <tr key={service.id || `service-${index}`}>
                          <td className="service-name-cell">
                            <textarea
                              ref={(el) => autoGrowTextarea(el)}
                              rows={1}
                              className="edit-service-input edit-service-name-input"
                              value={service.name || ''}
                              onChange={(e) => handleServiceChange(index, 'name', e.target.value)}
                              placeholder="Enter service name"
                              title={service.name || ''}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="edit-service-input"
                              value={service.quantity ?? 1}
                              onChange={(e) => handleServiceChange(index, 'quantity', e.target.value)}
                              min="1"
                              step="1"
                              placeholder="1"
                              title="Quantity"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="edit-service-input"
                              value={service.serviceCharge ?? ''}
                              onChange={(e) => handleServiceChange(index, 'serviceCharge', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0"
                              title="Unit service charge (total = charge × qty)"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="edit-service-input"
                              value={service.challanaAmount ?? ''}
                              onChange={(e) => handleServiceChange(index, 'challanaAmount', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0"
                              title="Unit challan (total = challan × qty)"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="edit-service-input edit-service-total"
                              value={service.total ?? ''}
                              onChange={(e) => handleServiceChange(index, 'total', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0"
                              title="Total (auto-calculated as qty × (Service Charge + Challan), but can be edited manually)"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="services-total-row">
                        <td><strong>Total</strong></td>
                        <td></td>
                        <td>
                          <strong>
                            {formatCurrency(
                              editForm.services.reduce(
                                (sum, s) =>
                                  sum + (Number(s.serviceCharge) || 0) * (Number(s.quantity) || 1),
                                0
                              )
                            )}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {formatCurrency(
                              editForm.services.reduce(
                                (sum, s) =>
                                  sum + (Number(s.challanaAmount) || 0) * (Number(s.quantity) || 1),
                                0
                              )
                            )}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {formatCurrency(
                              editForm.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
                            )}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="edit-services-empty">
                  <p>No services found. Services will appear here when available.</p>
                </div>
              )}
            </div>

            {/* Customer Section */}
            <div className="edit-customer-section">
              <h4 className="section-title">Customer</h4>
              <div className="customer-form-grid">
                <label className="form-field">
                  <span className="field-label">Customer Name</span>
                  <input 
                    type="text" 
                    value={editForm.customerName} 
                    onChange={(e) => handleEditChange('customerName', e.target.value)} 
                    className="form-input"
                  />
                </label>
                <label className="form-field">
                  <span className="field-label">Customer Phone</span>
                  <input 
                    type="tel" 
                    value={editForm.customerPhone} 
                    onChange={(e) => handleEditChange('customerPhone', e.target.value)} 
                    className="form-input"
                  />
                </label>
                <div className="form-field">
                  <ProfessionSelectField
                    value={editForm.profession || ''}
                    onChange={(profession) => handleEditChange('profession', profession)}
                    className="form-input"
                    labelClassName="field-label"
                    optional
                  />
                </div>
                <label className="form-field form-field-full">
                  <span className="field-label">Address</span>
                  <input 
                    type="text" 
                    value={editForm.customerAddress || ''} 
                    onChange={(e) => handleEditChange('customerAddress', e.target.value)} 
                    placeholder="Enter address" 
                    className="form-input"
                  />
                </label>
                <label className="form-field">
                  <span className="field-label">City/Village</span>
                  <input 
                    type="text" 
                    value={editForm.customerCity || ''} 
                    onChange={(e) => handleEditChange('customerCity', e.target.value)} 
                    placeholder="Enter city or village" 
                    className="form-input"
                  />
                </label>
                <label className="form-field">
                  <span className="field-label">PIN Code</span>
                  <input 
                    type="text" 
                    value={editForm.customerPincode || ''} 
                    onChange={(e) => handleEditChange('customerPincode', e.target.value)} 
                    placeholder="Enter PIN code" 
                    maxLength="10" 
                    className="form-input"
                  />
                </label>
              </div>
            </div>

            {/* Payment Summary Section */}
            <div className="payment-summary-section">
              <h4 className="section-title">PAYMENT SUMMARY</h4>
              <div className="payment-summary-cards">
                <div className="payment-summary-card">
                  <span className="payment-label">PREVIOUS PAID</span>
                  <span className="payment-value previous">{formatCurrency(editForm.previousPaidAmount || 0)}</span>
                </div>
                <div className="payment-summary-card">
                  <span className="payment-label">CURRENT TOTAL</span>
                  <span className="payment-value total">
                    {formatCurrency(
                      editForm.services
                        ? editForm.services.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
                        : editBill.totalAmount || 0
                    )}
                  </span>
                </div>
                <div className="payment-summary-card">
                  <span className="payment-label">TOTAL PAID</span>
                  <span className="payment-value paid">{formatCurrency(editForm.paidAmount || 0)}</span>
                </div>
                <div className="payment-summary-card">
                  <span className="payment-label">REMAINING BALANCE</span>
                  <span className={`payment-value ${editForm.dueAmount > 0 ? 'due' : 'paid'}`}>
                    {formatCurrency(editForm.dueAmount || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="payment-methods-section">
              <h4 className="section-title">Payment Methods</h4>
              <div className="payment-methods-grid">
                <label className="form-field">
                  <span className="field-label">Cash</span>
                  <input
                    type="number"
                    value={editForm.paymentMethods?.cash || 0}
                    onChange={(e) => handlePaymentMethodChange('cash', e.target.value)}
                    min="0"
                    step="0.01"
                    className="form-input"
                  />
                </label>
                <label className="form-field">
                  <span className="field-label">UPI</span>
                  <input
                    type="number"
                    value={editForm.paymentMethods?.upi || 0}
                    onChange={(e) => handlePaymentMethodChange('upi', e.target.value)}
                    min="0"
                    step="0.01"
                    className="form-input"
                  />
                </label>
                <label className="form-field">
                  <span className="field-label">Card</span>
                  <input
                    type="number"
                    value={editForm.paymentMethods?.card || 0}
                    onChange={(e) => handlePaymentMethodChange('card', e.target.value)}
                    min="0"
                    step="0.01"
                    className="form-input"
                  />
                </label>
                <label className="form-field">
                  <span className="field-label">Net Banking</span>
                  <input
                    type="number"
                    value={editForm.paymentMethods?.aeps || 0}
                    onChange={(e) => handlePaymentMethodChange('aeps', e.target.value)}
                    min="0"
                    step="0.01"
                    className="form-input"
                  />
                </label>
              </div>
              <div style={{ marginTop: '14px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={razorpayBusy || !(Number(editForm.dueAmount) > 0)}
                  onClick={() => void handleRazorpayPayBillDue()}
                >
                  {razorpayBusy ? 'Processing…' : `Pay balance with Razorpay (${formatCurrency(editForm.dueAmount)})`}
                </button>
                <small className="field-hint" style={{ display: 'block', marginTop: '8px' }}>
                  Opens Razorpay for the full remaining balance. After success, the amount is added under UPI and the bill is saved automatically.
                </small>
              </div>
            </div>

            {/* Remarks Section */}
            <div className="remarks-section-card">
              <h4 className="section-title">Counter Notes</h4>
              <textarea
                rows={2}
                value={editForm.remarks}
                onChange={(e) => handleEditChange('remarks', e.target.value)}
                placeholder="Internal note for the next staff member (optional)"
                className="form-textarea"
              ></textarea>
              <small className="field-hint">Separate from work status — use for handoff context only.</small>
            </div>

            {/* Work Status Section */}
            <div className="work-status-section">
              <div className="work-status-header">
                <div>
                  <h4 className="section-title">Work Status</h4>
                  <p className="section-description">
                    Mark <strong>Pending</strong> only when something still blocks delivery. Otherwise leave as Completed.
                  </p>
                </div>
                <span
                  className={`status-default-chip ${
                    editForm.isAdvance
                      ? 'is-advance'
                      : editForm.workStatus === 'pending'
                        ? 'is-pending'
                        : editForm.workStatus === 'cancelled'
                          ? 'is-cancelled'
                          : 'is-completed'
                  }`}
                >
                  <i
                    className={`fas ${
                      editForm.isAdvance
                        ? 'fa-wallet'
                        : editForm.workStatus === 'pending'
                          ? 'fa-hourglass-half'
                          : editForm.workStatus === 'cancelled'
                            ? 'fa-ban'
                            : 'fa-check-circle'
                    }`}
                  ></i>
                  {editForm.isAdvance
                    ? 'ADVANCE'
                    : editForm.workStatus === 'pending'
                      ? 'Needs follow-up'
                      : editForm.workStatus === 'cancelled'
                        ? 'Cancelled'
                        : 'Ready / done'}
                </span>
              </div>

              <div className="edit-service-context" title={editServiceSummary}>
                <div className="edit-service-context-meta">
                  <span className="edit-bill-no">{editBill.billNo || editBill.id}</span>
                  <span className="edit-service-label">Service</span>
                </div>
                <p className="edit-service-names">{editServiceSummary}</p>
              </div>

              {(editForm.legacyWorkStatus === 'under_processing' || editForm.legacyWorkStatus === 'cancelled') &&
                editForm.workStatus !== editForm.legacyWorkStatus && (
                  <p className="legacy-status-hint">
                    Previously stored as{' '}
                    <strong>
                      {WORK_STATUS_OPTIONS.find((o) => o.value === editForm.legacyWorkStatus)?.label ||
                        editForm.legacyWorkStatus}
                    </strong>
                    . Saving will update to the status you select below.
                  </p>
                )}

              <div className="work-status-radios" role="radiogroup" aria-label="Work status">
                {EDIT_WORK_STATUS_OPTIONS.map((status) => (
                  <label
                    key={status.value}
                    className={`work-status-radio ${editForm.workStatus === status.value ? 'active' : ''} status-${status.value}`}
                  >
                    <input
                      type="radio"
                      name="workStatus"
                      value={status.value}
                      checked={editForm.workStatus === status.value}
                      onChange={() => setEditWorkStatus(status.value)}
                    />
                    <span className="radio-dot" aria-hidden="true"></span>
                    <span className="radio-copy">
                      <strong>{status.label}</strong>
                      <span>{status.description}</span>
                    </span>
                  </label>
                ))}
                {editForm.workStatus === 'cancelled' && (
                  <label className="work-status-radio active status-cancelled">
                    <input
                      type="radio"
                      name="workStatus"
                      value="cancelled"
                      checked
                      onChange={() => setEditWorkStatus('cancelled')}
                    />
                    <span className="radio-dot" aria-hidden="true"></span>
                    <span className="radio-copy">
                      <strong>Cancelled</strong>
                      <span>Kept for this bill — choose Pending or Completed to reopen</span>
                    </span>
                  </label>
                )}
              </div>

              <label
                className={`edit-advance-toggle${editForm.isAdvance ? ' is-active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={!!editForm.isAdvance}
                  disabled={!!editBill.refundedAt}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleEditChange('isAdvance', checked);
                    if (checked && editForm.workStatus === 'completed') {
                      setEditWorkStatus('pending');
                    }
                  }}
                />
                <span className="edit-advance-copy">
                  <strong>Advance amount</strong>
                  <span>
                    Excluded from today / counter sales until work status is Completed
                  </span>
                </span>
              </label>

              {editForm.isAdvance && (
                <p className="edit-advance-hint">
                  {editForm.workStatus === 'completed'
                    ? 'Saving as Completed will add this bill to counter sales on today.'
                    : 'Update to Completed when work is done to count in counter sales.'}
                </p>
              )}

              {editForm.workStatus === 'pending' && (
                <div className="pending-remark-panel">
                  <label className="form-field work-remark-field-inline">
                    <span className="field-label">
                      What is pending? <em className="required-mark">Required</em>
                    </span>
                    <textarea
                      ref={pendingRemarkRef}
                      rows={2}
                      value={editForm.workRemark}
                      onChange={(e) => handleEditChange('workRemark', e.target.value)}
                      placeholder="e.g., Waiting for customer Aadhaar copy / PF portal OTP"
                      className="form-textarea pending-remark-textarea"
                      required
                    ></textarea>
                  </label>
                  <div className="pending-reason-chips" aria-label="Quick pending reasons">
                    {PENDING_REASON_CHIPS.map((reason) => (
                      <button
                        type="button"
                        key={reason}
                        className={`pending-reason-chip ${editForm.workRemark === reason ? 'active' : ''}`}
                        onClick={() => applyPendingReason(reason)}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                  <small className="field-hint">
                    Shown on Pending Works so the next shift knows exactly what to chase.
                  </small>
                </div>
              )}
            </div>

            {/* Cancel & Refund — simple presets */}
            <div className="cancel-refund-section">
              <div className="cancel-refund-header">
                <div>
                  <h4 className="section-title">Cancel bill</h4>
                  <p className="section-description">
                    Refund the customer (optional fee). Bill stays in records.
                  </p>
                </div>
                {!editBill.refundedAt && (
                  <button
                    type="button"
                    className={`btn btn-outline-danger cancel-toggle-btn${cancelPanelOpen ? ' active' : ''}`}
                    onClick={() => setCancelPanelOpen((open) => !open)}
                    disabled={loading || cancelBusy}
                  >
                    <i className="fas fa-ban"></i>
                    {cancelPanelOpen ? 'Hide' : 'Cancel'}
                  </button>
                )}
              </div>

              {editBill.refundedAt ? (
                <div className="cancel-refund-done">
                  <div className="cancel-refund-done-badge">
                    <i className="fas fa-check-circle"></i> Cancelled &amp; refunded
                  </div>
                  <p className="cancel-refund-summary">
                    Refunded {formatCurrency(editBill.refundAmount)}
                    {Number(editBill.processingFeeRetained) > 0
                      ? ` · kept ${formatCurrency(editBill.processingFeeRetained)} fee`
                      : ''}
                    {editBill.refundMethod
                      ? ` · via ${String(editBill.refundMethod).toUpperCase()}`
                      : ''}
                  </p>
                  {editBill.refundReason && (
                    <p className="cancel-refund-reason-line">{editBill.refundReason}</p>
                  )}
                </div>
              ) : cancelPanelOpen ? (
                <div className="cancel-refund-panel">
                  <div className="cancel-charge-row">
                    <div className="cancel-charge-box">
                      <span>Service charge</span>
                      <strong>
                        {formatCurrency(
                          cancelRefundPreview.serviceChargeTotal || cancelRefundPreview.paidAmount
                        )}
                      </strong>
                    </div>
                    <label className="cancel-charge-box is-fee">
                      <span>Processing charge</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="cancel-fee-input"
                        value={cancelFee}
                        onChange={(e) => setCancelFee(e.target.value)}
                        disabled={cancelBusy}
                      />
                    </label>
                    <div className="cancel-charge-box is-refund">
                      <span>Refund (deduct)</span>
                      <strong>{formatCurrency(cancelRefundPreview.refundAmount)}</strong>
                    </div>
                  </div>

                  <p className="cancel-refund-summary">
                    ₹{Number(cancelRefundPreview.refundAmount || 0).toFixed(0)} deducted from counter
                    total on <strong>bill date</strong>
                    {editBill.billDate || editBill.bill_date
                      ? ` (${new Date(editBill.billDate || editBill.bill_date).toLocaleDateString('en-IN')})`
                      : ''}
                    . Today&apos;s totals stay unchanged unless bill date is today.
                  </p>

                  {cancelRefundPreview.challanaTotal > 0 && (
                    <p className="cancel-refund-challan-note">
                      <i className="fas fa-info-circle"></i>
                      Govt challan {formatCurrency(cancelRefundPreview.challanaTotal)} cannot be kept as
                      shop fee (max keep {formatCurrency(cancelRefundPreview.maxRetainable)}).
                    </p>
                  )}

                  <div className="pending-reason-chips" aria-label="Cancel reason">
                    {CANCEL_REASON_CHIPS.map((reason) => (
                      <button
                        type="button"
                        key={reason}
                        className={`pending-reason-chip cancel-reason-chip ${
                          cancelReason === reason ? 'active' : ''
                        }`}
                        onClick={() => setCancelReason(reason)}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>

                  <label className="form-field">
                    <span className="field-label">Other reason (optional)</span>
                    <input
                      type="text"
                      className="form-input"
                      value={
                        CANCEL_REASON_CHIPS.includes(cancelReason) ? '' : cancelReason
                      }
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Type a custom reason if needed"
                    />
                  </label>

                  <div className="cancel-refund-actions">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => void handleCancelWithRefund()}
                      disabled={cancelBusy || loading || !String(cancelReason || '').trim()}
                    >
                      {cancelBusy ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Cancelling…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-ban"></i> Confirm cancel
                          {cancelRefundPreview.refundAmount > 0
                            ? ` · refund ${formatCurrency(cancelRefundPreview.refundAmount)}`
                            : ''}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="cancel-refund-teaser">
                  Enter processing charge — refund = service charge − processing charge, deducted on
                  bill date.
                </p>
              )}
            </div>
            </div>

            {/* Modal Actions */}
            <div className="edit-modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditBill(null)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmit}
                disabled={loading || Boolean(editBill.refundedAt) || cancelPanelOpen}
                title={
                  editBill.refundedAt
                    ? 'Refunded bills are locked'
                    : cancelPanelOpen
                      ? 'Finish or hide cancel panel first'
                      : undefined
                }
              >
                {loading ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewBills;















